import { apiClient } from './apiClient';

export interface PickableUser {
  id: number;
  name: string;
  email: string;
  role_id: number;
  role_name: string;
}

export interface TaskMember {
  user_id: number;
  member_type: 'participant' | 'observer';
  name: string | null;
}

export interface WorkflowTask {
  id: number;
  project_id: number;
  parent_task_id: number | null;
  kind: 'build' | 'qa' | 'deliver' | 'other';
  title: string | null;
  assignee_id: number;
  assignee_name: string | null;
  created_by: number;
  created_by_name: string | null;
  status: 'todo' | 'in_progress' | 'qa_complete' | 'done' | 'failed';
  requirements: string | null;
  priority: 'low' | 'medium' | 'high' | null;
  due_date: string | null;
  sla_label: string;
  created_at: string;
  updated_at: string;
  members: TaskMember[];
}

export interface OverviewSubtask {
  task_id: number;
  project_id: number;
  project_summary: string;
  projectid: string;
  project_stage: string;
  brand_name: string | null;
  project_created_at: string | null;
  project_created_by_name: string | null;
  project_assignee_name: string | null;
  title: string | null;
  status: string;
  kind: string;
  priority: 'low' | 'medium' | 'high' | null;
  sla_label: string;
}

export interface OverviewProject {
  id: number;
  projectid: string;
  summary: string;
  brand_name: string;
  stage: string;
  updated_at: string;
  assignee_name: string | null;
  file_count: number;
  sla_label: string;
}

export interface FDCOverview {
  active_projects: number;
  at_risk_slas: number;
  delivered_this_week: number;
  my_open_subtasks: OverviewSubtask[];
  my_projects: OverviewProject[];
  recent_projects: OverviewProject[];
}

export interface TaskListResponse {
  project_id: number;
  stage: string;
  tasks: WorkflowTask[];
  project_members: TaskMember[];
}

const taskService = {
  getOverview: async (): Promise<FDCOverview> => {
    const response = await apiClient.get<FDCOverview>('fdc/tasks/overview');
    return response.data;
  },

  listPickableUsers: async (role?: string, search?: string, excludeRoles?: string[]): Promise<PickableUser[]> => {
    const params: Record<string, string> = {};
    if (role) params.role = role;
    if (search) params.search = search;
    if (excludeRoles && excludeRoles.length > 0) params.exclude_roles = excludeRoles.join(',');
    const response = await apiClient.get<PickableUser[]>('fdc/tasks/users', params);
    return response.data;
  },

  getUsersByIds: async (ids: number[]): Promise<PickableUser[]> => {
    if (ids.length === 0) return [];
    const response = await apiClient.get<PickableUser[]>('fdc/tasks/users', { ids: ids.join(',') });
    return response.data;
  },

  getTasksByProject: async (projectId: number): Promise<TaskListResponse> => {
    const response = await apiClient.get<TaskListResponse>(`fdc/tasks/by-project/${projectId}`);
    return response.data;
  },

  myOpenTasks: async (): Promise<WorkflowTask[]> => {
    const response = await apiClient.get<WorkflowTask[]>('fdc/tasks/my-open');
    return response.data;
  },

  createSubtask: async (payload: {
    project_id: number;
    parent_task_id: number | null;
    kind: 'build' | 'qa' | 'deliver' | 'other';
    assignee_id: number;
    title?: string;
    requirements?: string;
    priority?: 'low' | 'medium' | 'high';
    due_date?: string;
    participant_ids?: number[];
    observer_ids?: number[];
  }): Promise<WorkflowTask> => {
    const response = await apiClient.post<WorkflowTask>('fdc/tasks/subtask', payload);
    return response.data;
  },

  updateTaskStatus: async (taskId: number, status: string): Promise<WorkflowTask> => {
    const response = await apiClient.patch<WorkflowTask>('fdc/tasks/status', { task_id: taskId, status });
    return response.data;
  },

  editSubtask: async (payload: {
    task_id: number;
    assignee_id: number;
    title?: string;
    requirements?: string;
    priority?: 'low' | 'medium' | 'high';
    due_date?: string;
  }): Promise<WorkflowTask> => {
    const response = await apiClient.patch<WorkflowTask>('fdc/tasks/subtask', payload);
    return response.data;
  },

  setProjectStage: async (projectId: number, stage: string, reason?: string): Promise<{ project_id: number; stage: string }> => {
    const response = await apiClient.post('fdc/tasks/set-stage', { project_id: projectId, stage, reason });
    return response.data;
  },

  addMembers: async (
    taskId: number,
    userIds: number[],
    memberType: 'participant' | 'observer'
  ): Promise<TaskMember[]> => {
    const response = await apiClient.post<TaskMember[]>(`fdc/tasks/${taskId}/members`, {
      user_ids: userIds,
      member_type: memberType,
    });
    return response.data;
  },

  removeMember: async (
    taskId: number,
    userId: number,
    memberType: 'participant' | 'observer'
  ): Promise<TaskMember[]> => {
    const response = await apiClient.delete<TaskMember[]>(
      `fdc/tasks/${taskId}/members/${userId}?member_type=${memberType}`
    );
    return response.data;
  },
};

export default taskService;
